/**
 * Type definitions mirroring backend Pydantic models (`backend/app/models.py`)
 * and the iOS Swift models. Backend wire format is snake_case; this file
 * exposes camelCase types and the API client does the conversion at the
 * boundary.
 *
 * If you add a field on the backend, mirror it here AND update the
 * mapper in api.ts.
 */

export type MessageRole = "user" | "assistant";

export interface Message {
  id: string;
  role: MessageRole;
  content: string;
  timestamp: string; // ISO8601
}

/** Conversation summary from GET /conversations */
export interface ConversationSummary {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
}

/** Full conversation from GET /conversations/{id} */
export interface ConversationDetail {
  id: string;
  title: string;
  mode?: string;
  modeAddendum?: string;
  messages: Message[];
  createdAt: string;
  updatedAt: string;
}

// ----------------------------------------------------------------------------
// Skills + recommendation
// ----------------------------------------------------------------------------

/** Trend values match backend's SkillState.trend exactly. */
export type SkillTrend = "up" | "steady" | "down";

export interface SkillState {
  skillId: string;
  name: string;
  category: string;
  sheetId: string;
  description: string;
  /** 0-100 — backend's idea of "where this skill sits today". */
  level: number;
  /** 0-95 — how confident the model is in `level`. */
  confidence: number;
  trend: SkillTrend;
  signalCount: number;
  lastAssessed: string | null;
  recentEvidence: string[];
}

/** One sample on a skill-level trajectory (GET /skills/history). */
export interface SkillHistoryPoint {
  /** ISO timestamp this sample reconstructs the level as-of. */
  date: string;
  /** 0-100 level on that date. */
  level: number;
  /** 0-95 confidence on that date. */
  confidence: number;
  /** Signals counted as of that date. */
  signalCount: number;
}

export interface SkillHistoryResponse {
  /** null for the overall trajectory; a skill id for a single-skill one. */
  skillId: string | null;
  /** Oldest-first; empty when there's no relevant signal yet. */
  points: SkillHistoryPoint[];
}

// --- Profile-as-asset summary (GET /profile-summary) ------------------------

export interface InvestmentStats {
  /** ISO timestamp of earliest activity, or null for a brand-new account. */
  activeSince: string | null;
  daysActive: number;
  drillsCompleted: number;
  signalsLogged: number;
  currentStreak: number;
  longestStreak: number;
}

export interface IdentitySkillRef {
  skillId: string;
  name: string;
  /** Present for strengths; omitted for the focus skill. */
  level?: number | null;
}

export interface PlayerIdentity {
  /** False until there's enough signal for a credible identity claim. */
  ready: boolean;
  topSkills: IdentitySkillRef[];
  topCategory: string | null;
  focusSkill: IdentitySkillRef | null;
}

export type MilestoneKind =
  | "started"
  | "drills"
  | "leak_report"
  | "graduation"
  | "overall";

export interface Milestone {
  date: string;
  kind: MilestoneKind;
  title: string;
  detail: string | null;
  skillId: string | null;
}

export interface OpenLeak {
  skillId: string | null;
  name: string | null;
  title: string;
  severity: string | null;
}

export interface ClosedLeak {
  skillId: string;
  name: string | null;
  title: string;
  flaggedAt: string;
  clearedBy: string;
}

export interface LeakHistory {
  open: OpenLeak[];
  closed: ClosedLeak[];
}

export interface ProfileSummary {
  investment: InvestmentStats;
  identity: PlayerIdentity;
  milestones: Milestone[];
  leaks: LeakHistory;
}

export interface SkillsResponse {
  skills: SkillState[];
  totalSignals: number;
}

export interface RecommendedDrill {
  skillId: string;
  skillName: string;
  strategy: string;
  kickoffMessage: string;
  rationale: string;
  /** Structured drill metadata — mirrors backend `RecommendedDrillResponse`.
   * `drillTarget` is either a canonical skill id or `"variety"`. Backend
   * composes the LLM-facing addendum prose from these structured fields
   * server-side; the client doesn't carry prose around any more. */
  drillTarget: string;
  drillDifficulty: DrillDifficulty;
}

/** Difficulty bands every drill carries. Three buckets matching the
 * prose hints already used by recommendation strategies + chatRoute
 * helpers. See backend `app/drill_meta.py`. */
export type DrillDifficulty = "foundational" | "default" | "edge_case";

/** The sentinel value for cross-skill ("Surprise Me" / cold-start
 * Fundamentals) drills. Backend rejects skill_signal blocks tagged
 * with this value — it's a drill-target only, not a skill. */
export const VARIETY_DRILL_TARGET = "variety";

// ----------------------------------------------------------------------------
// Leak detection
// ----------------------------------------------------------------------------

export type LeakSeverity = "high" | "medium" | "low";

export interface LeakPattern {
  id: string;
  title: string;
  description: string;
  severity: LeakSeverity;
  evidence: string[];
  recommendedAction: string;
  recommendedSkill: string;
}

export interface LeakReport {
  patterns: LeakPattern[];
  recommendedFocus: string;
  signalCount: number;
  generatedAt: string;
  insufficientSignals: boolean;
  /** The signal-total snapshot at the time this report was generated.
   *  Used to compute the "X / 10 till next" UI without re-querying. */
  signalCountAtLastReport: number;
  /** Countdown to the next automatic regeneration. Zero means the next
   *  view of the report will regenerate. */
  signalsUntilNextReport: number;
}

// ----------------------------------------------------------------------------
// Study sheets
// ----------------------------------------------------------------------------

export interface SheetMeta {
  id: string;
  displayName: string;
  number: number;
  category: string;
}

/** Canonical category order; mirrors iOS Sheet.Category. */
export const SHEET_CATEGORY_ORDER = [
  "GTO Foundations",
  "Opponent Archetypes",
  "Reference",
];

// ----------------------------------------------------------------------------
// Chat streaming
// ----------------------------------------------------------------------------

export type ChatStreamEvent =
  | { kind: "meta"; conversationId: string }
  | { kind: "token"; chunk: string }
  | { kind: "done" }
  | { kind: "error"; message: string };

/** Modes the backend understands for new conversations. Mirrors iOS
 *  SessionMode exactly (raw values match what the backend persists). */
export type SessionMode =
  | "spot_drill"
  | "concept_deep_dive"
  | "archetype_drill"
  | "switching_framework"
  | "just_ask"
  // Session-tracker coaching modes — conversational (no constructed spot),
  // launched from the tracker. The backend injects [SESSION FACTS] for these.
  | "session_warmup"
  | "session_debrief"
  | "session_consult"
  // Hand Analyzer — a one-off graded review of a user-built hand. The backend
  // injects [HAND REPLAY] for this mode; no constructed spot.
  | "hand_analysis";

export interface DrillOption {
  label: string;
  subtitle?: string;
  kickoff: string;
  /** Machine-readable drill target — canonical skill id or `"variety"`.
   * Required for drill-producing options (spot_drill, archetype_drill,
   * switching_framework) so library lookup has a clean handle. Omit for
   * non-drill options like Concept Deep Dive (no spots). */
  drillTarget?: string;
  drillDifficulty?: DrillDifficulty;
}

export interface ModeInfo {
  title: string;
  subtitle: string;
  icon: string;
  description: string;
  /** Kickoff sent when the user picks "Surprise me" within this mode. */
  surpriseKickoff: string;
  options: DrillOption[];
}

export const SESSION_MODES: Record<SessionMode, ModeInfo> = {
  spot_drill: {
    title: "Spot Drill",
    subtitle: "Focused practice on specific decisions",
    icon: "🎯",
    description:
      "Get a constructed spot, decide your action, and dig into the why.",
    surpriseKickoff:
      "Vary the spots: different skills, positions, and streets each drill. I'm looking for variety today, not focused training on a single decision type.",
    options: [
      { label: "Preflop opening decision", kickoff: "Drill me on a preflop opening decision.", drillTarget: "preflop_opens", drillDifficulty: "default" },
      { label: "Preflop 3-bet / 4-bet", kickoff: "Drill me on a preflop 3-bet or 4-bet decision.", drillTarget: "preflop_3betting", drillDifficulty: "default" },
      { label: "Flop c-bet (in position)", kickoff: "Drill me on a flop c-betting decision in position.", drillTarget: "flop_cbet_choice", drillDifficulty: "default" },
      { label: "Flop facing c-bet (OOP)", kickoff: "Drill me on a flop defending decision OOP vs a c-bet.", drillTarget: "flop_facing_cbet", drillDifficulty: "default" },
      { label: "Turn barreling", kickoff: "Drill me on a turn barreling decision.", drillTarget: "turn_barreling", drillDifficulty: "default" },
      { label: "Turn defending", kickoff: "Drill me on a turn defending decision.", drillTarget: "turn_defending", drillDifficulty: "default" },
      { label: "River value/bluff", kickoff: "Drill me on a river value-bet or bluff decision.", drillTarget: "river_value_betting", drillDifficulty: "default" },
      { label: "River bluff catching", kickoff: "Drill me on a river bluff-catching decision.", drillTarget: "river_bluff_catching", drillDifficulty: "default" },
      { label: "3-bet pot decision", kickoff: "Drill me on a postflop decision in a 3-bet pot.", drillTarget: "threebet_pots", drillDifficulty: "default" },
      { label: "Facing aggression", kickoff: "Drill me on a spot where I'm facing a raise, check-raise, or donk bet.", drillTarget: "facing_aggression", drillDifficulty: "default" },
    ],
  },
  concept_deep_dive: {
    title: "Concept Deep Dive",
    subtitle: "Build the framework, not just the rep",
    icon: "📚",
    description:
      "Pick a concept; the coach explains the mental model, the math, and the table-level read.",
    surpriseKickoff: "Concept deep-dive: surprise me. Pick a concept I should understand better.",
    options: [
      { label: "Range advantage vs. nut advantage", subtitle: "How they differ and when each drives the decision.", kickoff: "Concept deep-dive: range advantage vs nut advantage." },
      { label: "MDF and minimum defense frequency", subtitle: "Why MDF isn't the same as your actual calling range.", kickoff: "Concept deep-dive: MDF and how it differs from your actual calling range." },
      { label: "Bluff-to-value ratios on the river", subtitle: "The math, and how to estimate it at the table.", kickoff: "Concept deep-dive: bluff-to-value ratios on rivers: the math and the read." },
      { label: "Polarized vs. merged ranges", subtitle: "When to use each and why sizing follows from it.", kickoff: "Concept deep-dive: polarized vs merged ranges and how they shape sizing." },
      { label: "Blockers and combinatorics", subtitle: "Counting combos and using card removal in real time.", kickoff: "Concept deep-dive: blockers and combinatorics in real-time decisions." },
      { label: "Equity realization", subtitle: "Why position changes what your raw equity is worth.", kickoff: "Concept deep-dive: equity realization and why position matters so much." },
      { label: "3-bet pot SPR dynamics", subtitle: "How lower SPRs change every postflop decision.", kickoff: "Concept deep-dive: 3-bet pot SPR dynamics." },
      { label: "Sizing tells", subtitle: "What your bet sizes leak and how to fix it.", kickoff: "Concept deep-dive: sizing tells and how to avoid them." },
      { label: "Cost-of-error in exploit play", subtitle: "Cheap exploits vs. expensive ones and the evidence each needs.", kickoff: "Concept deep-dive: the cost-of-error matrix in exploit play." },
      { label: "Multiway adjustments", subtitle: "How the math shifts when more than two players see a flop.", kickoff: "Concept deep-dive: multiway adjustments to GTO play." },
    ],
  },
  archetype_drill: {
    title: "Archetype Drill",
    subtitle: "Play a hand against a specific opponent type",
    icon: "🃏",
    description:
      "Pick a villain archetype; the coach constructs a spot calibrated to that opponent's tendencies.",
    surpriseKickoff:
      "Vary the archetypes: different opponent types each drill. I want variety in who I'm playing against, not focused practice on one archetype.",
    options: [
      { label: "The Station", subtitle: "Calls too much, bluffs too little.", kickoff: "Drill me on a hand vs The Station.", drillTarget: "station_play", drillDifficulty: "default" },
      { label: "The Limper / Whale", subtitle: "Passive preflop, pays off postflop.", kickoff: "Drill me on a hand vs The Limper / Whale.", drillTarget: "limper_whale_play", drillDifficulty: "default" },
      { label: "The Nit", subtitle: "Folds too much, only bets the nuts.", kickoff: "Drill me on a hand vs The Nit.", drillTarget: "nit_play", drillDifficulty: "default" },
      { label: "The Maniac / Spewer", subtitle: "Over-aggression, undisciplined bluffs.", kickoff: "Drill me on a hand vs The Maniac / Spewer.", drillTarget: "maniac_play", drillDifficulty: "default" },
      { label: "The Tilted Player", subtitle: "Temporary state, time-limited window.", kickoff: "Drill me on a hand vs The Tilted Player.", drillTarget: "tilted_player_play", drillDifficulty: "default" },
      // No `solid_reg_play` skill exists in the taxonomy (deliberately
      // dropped — see 2026-05-22 session). Drill still works as a
      // free-form LLM-generated spot, just without library lookup.
      // drill_target left unset → backend won't try a library hit.
      { label: "The Solid Reg", subtitle: "Close to GTO: hunt specific leaks.", kickoff: "Drill me on a hand vs The Solid Reg." },
    ],
  },
  switching_framework: {
    title: "Switching Framework",
    subtitle: "When the read changes, you change",
    icon: "🔄",
    description:
      "Drill the moment a read shifts, and the discipline of revising your plan when it does.",
    surpriseKickoff:
      "Vary the scenarios: different read-shift situations each drill. I want variety in the framework challenges I face, not the same type repeated.",
    options: [
      { label: "Read shifts mid-session", subtitle: "A villain just contradicted the archetype you'd tagged.", kickoff: "Drill me on a scenario where a villain just contradicted the archetype I had on them.", drillTarget: "switching_framework", drillDifficulty: "default" },
      { label: "Tilt onset", subtitle: "A reg just took a bad beat. Window opens.", kickoff: "Drill me on a scenario where a reg just took a bad beat and is starting to tilt.", drillTarget: "switching_framework", drillDifficulty: "default" },
      { label: "Your image changes", subtitle: "You just got caught bluffing / showed a monster.", kickoff: "Drill me on a scenario where my image just changed: I got caught bluffing or showed a monster.", drillTarget: "switching_framework", drillDifficulty: "default" },
      { label: "Multiway with mixed archetypes", subtitle: "Different villains, different correct exploits, one pot.", kickoff: "Drill me on a multiway pot scenario with different archetypes in it.", drillTarget: "switching_framework", drillDifficulty: "default" },
      { label: "Stale read", subtitle: "Your read on someone is 2 hours old. Trust or revert?", kickoff: "Drill me on a scenario where my read on a villain is 2 hours old and they haven't confirmed it since.", drillTarget: "switching_framework", drillDifficulty: "default" },
    ],
  },
  just_ask: {
    title: "Just Ask",
    subtitle: "Open conversation with the coach",
    icon: "💬",
    description: "Free-form chat: bring a hand, a concept, or a question.",
    surpriseKickoff: "",
    options: [],
  },
  // Session-tracker coaching modes. Conversational; not in DRILL_MODES, so
  // they never render on the Drills grid. Titles are used for the chat header.
  session_warmup: {
    title: "Session Warmup",
    subtitle: "Get your head right before you sit down",
    icon: "🔥",
    description: "A quick pre-session check-in to set one clear intention.",
    surpriseKickoff: "",
    options: [],
  },
  session_debrief: {
    title: "Session Debrief",
    subtitle: "Cool down and find the leaks",
    icon: "🧊",
    description: "Walk through the session you just logged with your coach.",
    surpriseKickoff: "",
    options: [],
  },
  session_consult: {
    title: "Coach Consult",
    subtitle: "Talk results, bankroll, and what to fix next",
    icon: "📊",
    description: "A strategic look at your real numbers and trajectory.",
    surpriseKickoff: "",
    options: [],
  },
  hand_analysis: {
    title: "Hand Analysis",
    subtitle: "Grade a hand you built, street by street",
    icon: "🔍",
    description: "The coach reviews your line against the exact facts.",
    surpriseKickoff: "",
    options: [],
  },
};

/** Modes that show up as drill cards on the Drills home. Just Ask is its
 *  own tab, not a drill mode. */
export const DRILL_MODES: SessionMode[] = [
  "spot_drill",
  "concept_deep_dive",
  "archetype_drill",
  "switching_framework",
];

/** Skill-level bands — the single source of band boundaries + names for the
 *  web app (the gauge color palette in `features/skills/lib/skillStats.ts`
 *  keys off these names). Backend mirrors the same boundaries/names in
 *  `prompting/prompt_blocks.py:_level_label` (what the coach says) and
 *  `skills/profile.py:BANDS` (milestone graduations) — keep them aligned.
 *  Highest band first. */
export const LEVEL_BANDS = [
  { min: 80, name: "Crushing" },
  { min: 60, name: "Sharp" },
  { min: 40, name: "Solid" },
  { min: 20, name: "Developing" },
  { min: 0, name: "Learning" },
] as const;

/** Bucket a 0-100 level into its band name. Derived from LEVEL_BANDS. */
export function levelLabel(level: number): string {
  const band = LEVEL_BANDS.find((b) => level >= b.min);
  return (band ?? LEVEL_BANDS[LEVEL_BANDS.length - 1]).name;
}

// ----------------------------------------------------------------------------
// Structured assistant content (parsed from message text)
// ----------------------------------------------------------------------------

/** A poker spot the assistant has constructed. Shape mirrors the backend's
 *  Spot JSON exactly — the model writes this; iOS + web both parse. */
export type Street = "preflop" | "flop" | "turn" | "river";

/** Card in wire format: 2-char string like "Ah", "Td", "Kc". */
export type CardToken = string;

export type SpotActionKind = "fold" | "check" | "call" | "bet" | "raise";

/** Single action in a spot's history. `amount` is in big blinds; nil
 *  for check/call/fold. */
export interface SpotHistoryEntry {
  actor: string;       // "Hero" or position label like "BTN"
  action: SpotActionKind;
  amount?: number;
  street?: Street;
}

export interface SpotStacks {
  hero: number;        // big blinds
  villain: number;     // big blinds
}

export interface SpotPayload {
  type: "spot";
  position: string;     // "BTN", "BB", "CO", etc.
  hand: CardToken[];    // typically 2 cards
  stacks: SpotStacks;
  history: SpotHistoryEntry[];
  street: Street;
  board: CardToken[];   // 0, 3, 4, or 5 cards
  pot: number;          // big blinds in the middle
  to_act: string;       // "Hero" or villain label
  question?: string;    // optional human-readable framing
}

/** Action emitted by the SpotCard's button row — what the user chose. */
export interface SpotAction {
  action: SpotActionKind;
  amount?: number;
  label?: string;
}

/** A choices option that LAUNCHES a focused drill instead of re-sending its
 *  label. `drill_target` is one of the 24 canonical skill ids (same set as
 *  skill_signal). The web validates it against `VALID_DRILL_TARGETS` and routes
 *  to a real `/drill/new` session; an unknown target degrades to a plain
 *  re-send button. `drill_difficulty` is reserved in the contract but not
 *  consumed yet (the web launches leak drills at "foundational"). */
export interface DrillChoiceOption {
  label: string;
  drill_target: string;
  drill_difficulty?: DrillDifficulty;
}

/** A multiple-choice block the assistant emitted. The prompt is the surrounding
 *  text (iOS-compatible — no separate prompt field). An option is either a
 *  plain string (tapping re-sends the label as the next message) or a
 *  `DrillChoiceOption` (tapping launches a drill). */
export interface ChoicesPayload {
  type: "choices";
  options: (string | DrillChoiceOption)[];
}

/** Hidden block — internal signal/notes the model emits but the UI doesn't show. */
export interface HiddenPayload {
  raw: string;
}

export type ContentSegment =
  | { kind: "text"; text: string }
  | { kind: "spot"; spot: SpotPayload }
  | { kind: "choices"; choices: ChoicesPayload }
  | { kind: "hidden"; payload: HiddenPayload };

// ---------------------------------------------------------------------------
// Session & bankroll tracker
// ---------------------------------------------------------------------------

export type GameType = "cash" | "tournament";
export type Mood = "calm" | "sharp" | "tired" | "tilted";
export type HandOutcome = "won" | "lost" | "chopped";
export type HandStreet = "preflop" | "flop" | "turn" | "river";
export type SampleConfidence = "noise" | "directional" | "meaningful";

export interface NotableHand {
  summary: string;
  skill?: string | null;
  outcome?: HandOutcome | null;
  street?: HandStreet | null;
}

/** One logged session. `live` = a warmup stub awaiting its result. */
export interface LiveSession {
  id: string;
  status: "live" | "complete";
  gameType: GameType;
  stakes: string | null;
  bigBlind: number | null;
  title: string | null;
  notes: string | null;
  location: string | null;
  currency: string;
  buyIn: number;
  cashOut: number;
  net: number;
  startedAt: string;
  durationMinutes: number;
  intention: string | null;
  intentionSkill: string | null;
  intentionHeld: boolean | null;
  moodBefore: Mood | null;
  moodAfter: Mood | null;
  tags: string[];
  notableHands: NotableHand[];
  debriefConversationId: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Deterministic aggregate stats — mirrors backend facts.session_stats. */
export interface SessionStats {
  sessionCount: number;
  totalHours: number;
  netTotal: number;
  netPerHour: number;
  bbPerHour: number | null;
  winRatePct: number;
  biggestWin: number;
  biggestLoss: number;
  currentStreak: number;
  longestWinStreak: number;
  longestLossStreak: number;
  stdDevPerSession: number;
  maxDrawdown: number;
  formNet: number;
  primaryStakes: string | null;
  confidence: SampleConfidence;
  sampleCaveat: string;
}

export interface BankrollPoint {
  at: string;
  sessionId: string;
  net: number;
  cumulative: number;
}

export type BreakdownDimension = "stake" | "location";

/** One row of a by-stake / by-location breakdown. */
export interface SegmentStats {
  key: string;
  stats: SessionStats;
}

export interface Breakdown {
  by: BreakdownDimension;
  segments: SegmentStats[];
}

/** Optional filter for the tracker-home query (tap-to-filter one segment). */
export interface SessionFilter {
  stake?: string;
  location?: string;
}

/** One-call tracker-home payload. */
export interface TrackerHome {
  sessions: LiveSession[];
  stats: SessionStats;
  bankroll: BankrollPoint[];
  openLiveSession: LiveSession | null;
}

/** Body for logging a session (one-step post-game). */
export interface LogSessionInput {
  gameType: GameType;
  stakes: string;
  buyIn: number;
  cashOut: number;
  durationMinutes: number;
  startedAt: string;
  title?: string | null;
  notes?: string | null;
  location?: string | null;
  currency?: string | null;
  intention?: string | null;
  intentionSkill?: string | null;
  moodBefore?: Mood | null;
  moodAfter?: Mood | null;
  tags?: string[];
  notableHands?: NotableHand[];
}

// ---------------------------------------------------------------------------
// MARK: - Hand Analyzer
//
// A user-authored hand built for coach analysis. camelCase in TS; the api.ts
// boundary maps to/from the snake_case wire (BuiltHand on the backend). The
// action `amount` is the absolute bet LEVEL for the street ("raise to 9bb" → 9),
// required for bet/raise — mirrors the spot `history` contract.
// ---------------------------------------------------------------------------

export type HandActionKind = "check" | "bet" | "call" | "raise" | "fold";
export type HandEndedOn =
  | "preflop"
  | "flop"
  | "turn"
  | "river"
  | "showdown";

export interface BuiltHandAction {
  actor: string; // "Hero" or a canonical seat
  action: HandActionKind;
  amount?: number; // absolute bet level; required for bet/raise
}

export interface BuiltHandVillain {
  position: string; // canonical seat
  cards: [string, string] | null; // null = unknown (not shown)
}

export interface BuiltHandStreets {
  preflop: { actions: BuiltHandAction[] };
  flop?: { board: [string, string, string]; actions: BuiltHandAction[] };
  turn?: { card: string; actions: BuiltHandAction[] };
  river?: { card: string; actions: BuiltHandAction[] };
}

export interface BuiltHand {
  heroPosition: string;
  heroCards: [string, string];
  effectiveStackBb: number;
  villains: BuiltHandVillain[]; // >= 1
  streets: BuiltHandStreets;
  endedOn: HandEndedOn;
  notes?: string;
}

/** Full saved hand (GET/POST /hands, GET /hands/{id}). */
export interface SavedHand {
  id: string;
  title: string | null;
  hand: BuiltHand;
  analysisConversationId: string | null;
  /** Set when the hand was attached to a logged session (tracker doorway). */
  sessionId: string | null;
  createdAt: string;
}

/** Lightweight list item for the Analyzer home (GET /hands). */
export interface HandSummary {
  id: string;
  title: string | null;
  summary: string;
  analyzed: boolean;
  sessionId: string | null;
  createdAt: string;
}

/** Patch body — complete a live stub or edit an existing session. */
export type UpdateSessionInput = Partial<LogSessionInput> & {
  status?: "complete";
  intentionHeld?: boolean | null;
};
